<template>
  <div class="safety-dashboard">
    <div class="row">
      <div class="card device-health" v-loading="equipmentLoading">
        <h3>设备运行情况总览</h3>
        <div class="health-top">
          <div class="health-text">
            <h2>设备综合健康度</h2>
            <p class="health-percent">{{ deviceHealthRate }}%</p>
          </div>
          <div class="health-chart" ref="healthChart"></div>
        </div>
        <div class="health-stats">
          <div class="stat-item" v-for="item in equipmentStats" :key="item.label">
            <p><span :style="{ color: item.color }">{{ item.label }}</span> {{ item.count }} 台</p>
          </div>
        </div>
      </div>

      <div class="card inspection-overview" v-loading="inspectionLoading">
        <h3>巡检情况总览</h3>
        <div class="inspection-top">
          <div class="inspection-chart" ref="inspectionChart"></div>
          <div class="inspection-text">
            <p>今日巡检次数</p>
            <p class="inspection-progress">{{ todayInspectionCount }} 次</p>
            <p class="inspection-abnormal">近 7 日累计 {{ inspectionTotalCount }} 次</p>
          </div>
        </div>
        <div class="inspection-map">
          {{ inspectionSummaryText }}
        </div>
      </div>
    </div>

    <div class="row">
      <div class="card accident-trend" v-loading="accidentLoading">
        <h3>安全事故趋势(近12个月)</h3>
        <div class="trend-chart" ref="trendChart"></div>
      </div>

      <div class="card personnel-abnormal" v-loading="personnelLoading">
        <h3>人员定位异常</h3>
        <div class="abnormal-stats">
          <p class="abnormal-title">实时异常统计: {{ personnelAbnormal.totalCount || 0 }} 人</p>
          <div class="stats-list">
            <div class="stat-item">报警中 {{ personnelAbnormal.totalCount || 0 }} 人</div>
            <div class="stat-item">待通知 {{ personnelAbnormal.pendingAlarmCount || 0 }} 人</div>
          </div>
        </div>
        <div class="abnormal-list">
          <h4>异常人员列表</h4>
          <ul v-if="personnelRecords.length">
            <li v-for="item in personnelRecords" :key="item.id">
              {{ item.employeeName || '未知人员' }} - {{ item.fenceName || '未知围栏' }}
              <span class="time">停留{{ item.stayDurationText || '-' }}</span>
            </li>
          </ul>
          <div v-else class="empty-text">暂无异常</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import 'echarts/theme/macarons'
import {
  getAccidentMonthlyTrend,
  getEquipmentRealtimeStatus,
  getInspectionCompletionStatus,
  getPersonnelRealtimeAbnormal
} from '../api'

export default {
  data() {
    return {
      equipmentLoading: false,
      inspectionLoading: false,
      accidentLoading: false,
      personnelLoading: false,
      equipmentStatusList: [],
      inspectionData: { dates: [], series: [] },
      accidentTrend: [],
      personnelAbnormal: { totalCount: 0, pendingAlarmCount: 0, records: [] },
      healthChartInstance: null,
      inspectionChartInstance: null,
      trendChartInstance: null
    }
  },
  computed: {
    equipmentTotal() {
      return this.equipmentStatusList.reduce((sum, item) => sum + this.toNumber(item.count), 0)
    },
    normalDeviceCount() {
      const normal = this.equipmentStatusList.find((item) => item.operationStatus === 0 || item.operationStatusLabel === '正常')
      return normal ? this.toNumber(normal.count) : 0
    },
    deviceHealthRate() {
      if (!this.equipmentTotal) return 0
      return Math.round((this.normalDeviceCount / this.equipmentTotal) * 100)
    },
    equipmentStats() {
      const colors = ['#28a745', '#dc3545', '#ffc107', '#6c757d', '#909399', '#409eff']
      return (this.equipmentStatusList || []).map((item, index) => ({
        label: item.operationStatusLabel || '未设置',
        count: this.toNumber(item.count),
        color: colors[index % colors.length]
      }))
    },
    inspectionTotalCount() {
      return (this.inspectionData.series || []).reduce((sum, series) => {
        return sum + (series.data || []).reduce((itemSum, value) => itemSum + this.toNumber(value), 0)
      }, 0)
    },
    todayInspectionCount() {
      const dates = this.inspectionData.dates || []
      const lastIndex = dates.length - 1
      if (lastIndex < 0) return 0
      return (this.inspectionData.series || []).reduce((sum, series) => sum + this.toNumber((series.data || [])[lastIndex]), 0)
    },
    inspectionSummaryText() {
      const count = (this.inspectionData.series || []).length
      return count ? `覆盖 ${count} 个巡检点位` : '暂无巡检数据'
    },
    personnelRecords() {
      return this.personnelAbnormal.records || []
    }
  },
  mounted() {
    this.initCharts()
    this.loadEquipment()
    this.loadInspection()
    this.loadAccidentTrend()
    this.loadPersonnelAbnormal()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.healthChartInstance && this.healthChartInstance.dispose()
    this.inspectionChartInstance && this.inspectionChartInstance.dispose()
    this.trendChartInstance && this.trendChartInstance.dispose()
  },
  methods: {
    toNumber(value) {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    },
    initCharts() {
      this.healthChartInstance = echarts.init(this.$refs.healthChart)
      this.inspectionChartInstance = echarts.init(this.$refs.inspectionChart)
      this.trendChartInstance = echarts.init(this.$refs.trendChart)
      this.renderHealthChart()
      this.renderInspectionChart()
      this.renderAccidentTrend()
    },
    loadEquipment() {
      this.equipmentLoading = true
      getEquipmentRealtimeStatus()
        .then((data) => {
          this.equipmentStatusList = Array.isArray(data) ? data : []
          this.renderHealthChart()
        })
        .catch(() => {
          this.equipmentStatusList = []
          this.renderHealthChart()
        })
        .finally(() => {
          this.equipmentLoading = false
        })
    },
    loadInspection() {
      this.inspectionLoading = true
      getInspectionCompletionStatus()
        .then((data) => {
          this.inspectionData = data || { dates: [], series: [] }
          this.renderInspectionChart()
        })
        .catch(() => {
          this.inspectionData = { dates: [], series: [] }
          this.renderInspectionChart()
        })
        .finally(() => {
          this.inspectionLoading = false
        })
    },
    loadAccidentTrend() {
      this.accidentLoading = true
      getAccidentMonthlyTrend(12)
        .then((data) => {
          this.accidentTrend = Array.isArray(data) ? data : []
          this.renderAccidentTrend()
        })
        .catch(() => {
          this.accidentTrend = []
          this.renderAccidentTrend()
        })
        .finally(() => {
          this.accidentLoading = false
        })
    },
    loadPersonnelAbnormal() {
      this.personnelLoading = true
      getPersonnelRealtimeAbnormal(5)
        .then((data) => {
          this.personnelAbnormal = data || { totalCount: 0, pendingAlarmCount: 0, records: [] }
        })
        .catch(() => {
          this.personnelAbnormal = { totalCount: 0, pendingAlarmCount: 0, records: [] }
        })
        .finally(() => {
          this.personnelLoading = false
        })
    },
    renderHealthChart() {
      if (!this.healthChartInstance) return
      const data = this.equipmentStats.length
        ? this.equipmentStats.map((item) => ({ name: item.label, value: item.count, itemStyle: { color: item.color } }))
        : [{ name: '暂无数据', value: 1, itemStyle: { color: '#dcdfe6' } }]
      this.healthChartInstance.setOption({
        series: [{ type: 'pie', radius: ['60%', '80%'], label: { show: false }, data }]
      }, true)
    },
    renderInspectionChart() {
      if (!this.inspectionChartInstance) return
      const done = this.todayInspectionCount
      const rest = done > 0 ? Math.max(0, this.inspectionTotalCount - done) : 1
      this.inspectionChartInstance.setOption({
        series: [{
          type: 'pie',
          radius: ['60%', '80%'],
          label: { show: false },
          data: [
            { name: '今日', value: done, itemStyle: { color: '#28a745' } },
            { name: '历史', value: rest, itemStyle: { color: '#6c757d' } }
          ]
        }]
      }, true)
    },
    renderAccidentTrend() {
      if (!this.trendChartInstance) return
      this.trendChartInstance.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: this.accidentTrend.map((item) => item.month) },
        yAxis: { type: 'value', minInterval: 1, name: '事故数' },
        series: [{
          name: '安全事故数',
          type: 'bar',
          data: this.accidentTrend.map((item) => item.count),
          itemStyle: { color: '#dc3545' }
        }]
      }, true)
    },
    handleResize() {
      this.healthChartInstance && this.healthChartInstance.resize()
      this.inspectionChartInstance && this.inspectionChartInstance.resize()
      this.trendChartInstance && this.trendChartInstance.resize()
    }
  }
}
</script>

<style scoped>
.safety-dashboard {
  padding: 0 20px 20px;
}
.row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  width: 100%;
}
.card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  min-width: 0;
}
.card h3 {
  font-size: 16px;
  margin: 0 0 20px;
  font-weight: 600;
  color: #333;
}
.health-top,
.inspection-top {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}
.health-text h2 {
  font-size: 18px;
  margin: 0 0 5px;
  color: #666;
}
.health-percent,
.inspection-progress {
  font-size: 24px;
  font-weight: 700;
  color: #007bff;
  margin: 0;
}
.health-chart,
.inspection-chart {
  width: 110px;
  height: 110px;
}
.health-stats,
.stats-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.stat-item {
  flex: 1 1 45%;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  font-size: 14px;
}
.inspection-text p {
  margin: 0 0 6px;
}
.inspection-abnormal {
  color: #dc3545;
}
.inspection-map {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 16px;
  color: #666;
}
.trend-chart {
  width: 100%;
  height: 310px;
}
.abnormal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 15px;
}
.abnormal-list h4 {
  margin: 18px 0 10px;
}
.abnormal-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.abnormal-list li {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}
.time {
  float: right;
  color: #dc3545;
}
.empty-text {
  color: #909399;
  padding: 12px 0;
}
@media (max-width: 900px) {
  .row {
    flex-direction: column;
  }
}
</style>
